/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useLayoutEffect, useRef, useState } from 'react';
import {
	Box,
	Card,
	CircularProgress,
	Collapse,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { Delete } from '@mui/icons-material';
import { formatDate, getElementLineHeight } from '@/utils/constants';
import EditIcon from '@mui/icons-material/Edit';

const getFirstTextElement = (root) => {
	if (!root) return null;

	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => {
			if (!node.textContent?.trim()) {
				return NodeFilter.FILTER_REJECT;
			}

			const parent = node.parentElement;

			if (!parent) {
				return NodeFilter.FILTER_REJECT;
			}

			const styles = window.getComputedStyle(parent);

			if (styles.display === 'none' || styles.visibility === 'hidden') {
				return NodeFilter.FILTER_REJECT;
			}

			return NodeFilter.FILTER_ACCEPT;
		},
	});

	const firstTextNode = walker.nextNode();

	return firstTextNode?.parentElement ?? null;
};

const EngagementsAccordion = ({
	title,
	expandedContent,
	content,
	contentPrefix,
	footerContent,
	expandedByDefault = false,
	previewWhenCollapsed = true,
	lastUpdated,
	loading,
	handleDelete,
	handleEdit,
	testid,
}) => {
	const theme = useTheme();

	const [expanded, setExpanded] = useState(expandedByDefault);

	const prefixRef = useRef(null);
	const textRef = useRef(null);
	const [collapsedSize, setCollapsedSize] = useState(
		theme.typography?.body1?.lineHeight ?? '6em',
	);

	const toggleExpanded = () => setExpanded((prev) => !prev);

	useLayoutEffect(() => {
		if (!previewWhenCollapsed) {
			setCollapsedSize(0);
			return;
		}

		if (contentPrefix && prefixRef.current) {
			const height = prefixRef.current.getBoundingClientRect().height;
			setCollapsedSize(`${height}px`);
			return;
		}

		if (!textRef.current) return;
		const firstTextElement = getFirstTextElement(textRef.current);

		const rootLineHeight = getElementLineHeight(textRef.current);
		const firstLineHeight = getElementLineHeight(firstTextElement);

		const height = Math.max(rootLineHeight, firstLineHeight);

		setCollapsedSize(`${height}px`);
	}, [contentPrefix, content, previewWhenCollapsed]);

	return (
		<Card
			data-testid={testid}
			elevation={theme.palette.mode === 'dark' ? 0 : 1}
			sx={{
				marginBottom: '12px',
				borderRadius: '4px',
				p: '1em',
				...(theme.palette.mode === 'dark' && {
					border: `1px solid ${theme.palette.divider}`,
				}),
			}}
		>
			<Stack
				direction={'row'}
				sx={{
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Stack
					direction={'row'}
					sx={{
						cursor: 'pointer',
						alignItems: 'center',
						// flex: 'none',
					}}
					onClick={toggleExpanded}
				>
					<ArrowForwardIosSharpIcon
						sx={{
							fontSize: '0.9rem',
							color: theme.palette.primary.main,
							transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
							transition: 'transform 0.3s ease',
							mr: '0.5em',
						}}
					/>
					<Typography component='div' variant='task_body'>
						{title}
					</Typography>
				</Stack>
				{(handleEdit || handleDelete) && (
					<Stack direction={'row'} sx={{ alignItems: 'center' }} spacing={'0.5em'}>
						{handleEdit && (
							<IconButton
								onClick={handleEdit}
								size='small'
								sx={{
									color: theme.palette.primary.main,
									position: 'relative',
									fontSize: '1.25rem !important',
								}}
							>
								<EditIcon size='small' sx={{ fontSize: '1.25rem !important' }} />
							</IconButton>
						)}
						{handleDelete && (
							<>
								{loading ? (
									<Box padding='0.76rem'>
										<CircularProgress size='1rem' />
									</Box>
								) : (
									<IconButton
										aria-label='delete'
										size='small'
										onClick={handleDelete}
										sx={{
											color: theme.palette.primary.main,
											position: 'relative',
											fontSize: '1.25rem !important',
										}}
									>
										<Delete
											size='small'
											sx={{ fontSize: '1.25rem !important' }}
										/>
									</IconButton>
								)}
							</>
						)}
					</Stack>
				)}
			</Stack>
			{(content || contentPrefix) && (expanded || previewWhenCollapsed) && (
				<Collapse in={expanded} collapsedSize={collapsedSize}>
					<Stack direction='row' alignItems='center' width='100%' height='100%'>
						{contentPrefix && (
							<Box ref={prefixRef} display='flex' alignItems='center' mr={1}>
								{contentPrefix}
							</Box>
						)}

						<Typography
							ref={textRef}
							variant='task_body'
							component='div'
							sx={{
								flexGrow: 1,
								display: '-webkit-box',
								width: '100%',
								overflow: 'hidden',
								WebkitBoxOrient: 'vertical',
								WebkitLineClamp: expanded ? 'unset' : 3,
								whiteSpace: 'normal',
							}}
						>
							{content}
						</Typography>
					</Stack>
				</Collapse>
			)}
			{expandedContent && (
				<Collapse in={expanded} timeout='auto' unmountOnExit>
					<Stack px='1em'>{expandedContent}</Stack>
				</Collapse>
			)}
			{(footerContent || lastUpdated) && (expanded || previewWhenCollapsed) && (
				<Stack
					direction={'row'}
					justifyContent={footerContent ? 'space-between' : 'flex-end'}
					alignItems={'center'}
					pt='1em'
					spacing={2}
				>
					{footerContent && footerContent}
					<Typography variant='task_updated'>
						{lastUpdated ? `Last updated: ${formatDate(lastUpdated, true)}` : ''}
					</Typography>
				</Stack>
			)}
		</Card>
	);
};

export default EngagementsAccordion;
